import { FunctionComponent, useCallback, useContext, useMemo, useState } from "react";
import { Grid, useMediaQuery, useTheme, Button, makeStyles, Theme } from "@material-ui/core";
import { ITopBarButtonProps, TopBarButton } from "./buttons/tob-bar-button";
import { ShopIcon } from "../../assets/icons/shop-icon";
import { PersonIcon } from "../../assets/icons/person-icon";
import { useTranslation } from "react-i18next";
import { useUser } from "../user/user-context";
import { ConfirmDialog } from "../dialog/confirm-dialog";
import { MenuIcon } from "../../assets/icons/menu-icon";
import { getLocalizedLinks } from "../../translations/links";
import { FavoritesIcon } from "../../assets/icons/favorites-icon";
import { IdCardIcon } from "../../assets/icons/id-card-icon";
import { NutritionContext } from "../../pages/nutrition/context/nutrition-context";
import { MenuItemCategory, MenuItemSubcategory } from "../../models/types/category-types";
import { ResultsIcon } from "../../assets/icons/results-icon";
// import { MagnifyIcon } from "../../assets/icons/magnify-icon";
import { LaboratoryIcon } from "../../assets/icons/laboratory-icon";

const useStyles = makeStyles((theme: Theme) => ({
  logoutButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 10,
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  mobileMenu: {
    borderRadius: 10,
  },
}));

interface ITopBarActionsProps {
  accordionOpen: boolean;
  onMenuButtonClicked: () => void;
}

export const TopBarActions: FunctionComponent<ITopBarActionsProps> = (props) => {
  const { onMenuButtonClicked } = props;
  const classes = useStyles();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { logout } = useUser();
  const { activeCategory, activeSubcategory, changeActiveCategories } = useContext(NutritionContext);
  const [logoutModalOpen, setLogoutModalOpen] = useState<boolean>(false);
  const toggleLogoutModal = useCallback(() => setLogoutModalOpen(!logoutModalOpen), [
    logoutModalOpen,
    setLogoutModalOpen,
  ]);
  const isMd = useMediaQuery(theme.breakpoints.up("md"));

  const productPageLink = useMemo(() => getLocalizedLinks(i18n).MY_DNA_PRODUCT_PAGE, [i18n]);

  const handleYourData = useCallback(() => {
    changeActiveCategories(MenuItemCategory.USER, MenuItemSubcategory.USER_DATA);
  }, [changeActiveCategories]);

  const handleFavourites = useCallback(() => {
    changeActiveCategories(MenuItemCategory.USER, MenuItemSubcategory.FAVOURITES);
  }, [changeActiveCategories]);

  const handleYourReports = useCallback(() => {
    changeActiveCategories(MenuItemCategory.USER, MenuItemSubcategory.USER_REPORTS);
  }, [changeActiveCategories]);

  const buttons: ITopBarButtonProps[] = [
    {
      text: t("app_bar.results"),
      icon: ResultsIcon,
      isActive: activeCategory !== MenuItemCategory.USER,
      onClick: () => {
        if (activeCategory === MenuItemCategory.USER) {
          changeActiveCategories(MenuItemCategory.RECOMMENDATIONS, MenuItemSubcategory.OVERVIEW);
        }
      },
    },
    // {
    //   text: t("app_bar.search"),
    //   icon: MagnifyIcon,
    //   onClick: () => {},
    // },
    {
      text: t("app_bar.shop"),
      icon: ShopIcon,
      onClick: () => {
        window.open(productPageLink, "_blank");
      },
    },
    {
      text: t("app_bar.profile"),
      icon: PersonIcon,
      isActive: activeCategory === MenuItemCategory.USER,
      subButtons: [
        {
          text: t("app_bar.favorites"),
          icon: FavoritesIcon,
          onClick: handleFavourites,
          isActive: activeSubcategory === MenuItemSubcategory.FAVOURITES,
        },
        {
          text: t("app_bar.your_data"),
          icon: IdCardIcon,
          onClick: handleYourData,
          isActive: activeSubcategory === MenuItemSubcategory.USER_DATA,
        },
        {
          text: t("app_bar.your_reports"),
          icon: LaboratoryIcon,
          onClick: handleYourReports,
          isActive: activeSubcategory === MenuItemSubcategory.USER_REPORTS,
        },
        {
          text: t("app_bar.logout"),
          onClick: toggleLogoutModal,
          className: classes.logoutButton,
          color: "primary",
          variant: "contained",
        },
      ],
    },
  ];

  return (
    <>
      <ConfirmDialog
        heading={t("app_bar.logout_modal.heading")}
        confirmText={t("general.logout")}
        onConfirm={logout}
        onClose={toggleLogoutModal}
        open={logoutModalOpen}
      />
      {isMd && (
        <Grid item container justify="center" alignItems="center">
          {buttons.map((buttonProps, index) => (
            <TopBarButton key={index} {...buttonProps} />
          ))}
        </Grid>
      )}
      {!isMd && (
        <Grid item container justify="space-between" alignItems="center">
          <Button id={"menu-button"} onClick={() => onMenuButtonClicked()}>
            <MenuIcon width={36} height={36} />
          </Button>
        </Grid>
      )}
    </>
  );
};
